import { useEffect, useState } from 'react';
import { Tabs, Form, Input, Button, Card, Switch, message, Space, Typography, Divider, Popconfirm, Tag, Select } from 'antd';
import { SettingOutlined, MailOutlined, CloudOutlined, MessageOutlined, SaveOutlined, DeleteOutlined, ApiOutlined, CheckCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { AdminService } from '../api/services/admin.service';
import { ExchangeService } from '../api/services/exchange.service';
import { UsersService } from '../api/services/users.service';
import { useAuth } from '../store/auth';

const { Title, Text } = Typography;

const INTEGRATIONS = [
  {
    key: 'nextcloud_storage',
    name: 'Nextcloud Хранилище',
    icon: <CloudOutlined />,
    fields: [
      { name: 'url', label: 'URL сервера', placeholder: 'https://files.sz-t.ru', required: true },
      { name: 'username', label: 'Логин администратора', placeholder: 'admin', required: true },
      { name: 'password', label: 'Пароль', placeholder: 'Введите пароль', required: true, isPassword: true },
    ],
  },
  {
    key: 'nextcloud_chat',
    name: 'Nextcloud Чат',
    icon: <MessageOutlined />,
    fields: [
      { name: 'url', label: 'URL сервера', placeholder: 'https://nextcloud.example.com', required: true },
      { name: 'username', label: 'Логин администратора', placeholder: 'admin', required: true },
      { name: 'password', label: 'Пароль', placeholder: 'Введите пароль', required: true, isPassword: true },
    ],
  },
];

export default function AdminPage() {
  const [forms] = useState({});
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const { user } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userCreds, setUserCreds] = useState(null);
  const [userCredsForm] = Form.useForm();

  const isAdmin = user?.role_level >= 5;

  useEffect(() => {
    if (isAdmin) {
      fetchIntegrations();
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const res = await UsersService.getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    }
  };

  const fetchUserCredentials = async (userId) => {
    try {
      const res = await ExchangeService.getAdminCredentials(userId);
      setUserCreds(res.data);
      if (res.data.configured) {
        userCredsForm.setFieldsValue({
          nc_username: res.data.nc_username,
          nc_password: '',
        });
      } else {
        userCredsForm.resetFields();
      }
    } catch (err) {
      message.error('Ошибка загрузки учётных данных');
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      fetchUserCredentials(userId);
    } else {
      setUserCreds(null);
      userCredsForm.resetFields();
    }
  };

  const handleSaveUserCredentials = async (values) => {
    if (!selectedUserId) {
      message.error('Выберите пользователя');
      return;
    }
    
    try {
      await ExchangeService.setAdminCredentials(selectedUserId, values.nc_username, values.nc_password);
      message.success('Учётные данные сохранены');
      fetchUserCredentials(selectedUserId);
    } catch (err) {
      message.error('Ошибка сохранения');
    }
  };

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const res = await AdminService.getIntegrations();
      const data = {};
      res.data.forEach(int => {
        data[int.service_key] = int;
      });
      setIntegrations(data);
    } catch (err) {
      message.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (serviceKey) => {
    const integration = INTEGRATIONS.find(i => i.key === serviceKey);
    if (!integration) return;

    setSaving(prev => ({ ...prev, [serviceKey]: true }));

    try {
      const form = forms[serviceKey];
      const values = await form.validateFields();
      
      const config = {};
      integration.fields.forEach(field => {
        if (values[field.name] !== undefined) {
          config[field.name] = values[field.name];
        }
      });

      const currentIntegration = integrations[serviceKey];
      const payload = {
        service_name: integration.name,
        config: config,
        is_active: currentIntegration?.is_active ?? false,
      };

      await AdminService.createOrUpdateIntegration(serviceKey, payload);
      message.success('Настройки сохранены');
      fetchIntegrations();
    } catch (err) {
      message.error('Ошибка сохранения');
    } finally {
      setSaving(prev => ({ ...prev, [serviceKey]: false }));
    }
  };

  const handleToggleActive = async (serviceKey, checked) => {
    try {
      const integration = INTEGRATIONS.find(i => i.key === serviceKey);
      const currentIntegration = integrations[serviceKey];
      
      if (!currentIntegration) {
        message.error('Сначала сохраните настройки');
        return;
      }

      const payload = {
        service_name: integration.name,
        config: currentIntegration.config,
        is_active: checked,
      };

      await AdminService.createOrUpdateIntegration(serviceKey, payload);
      message.success(checked ? 'Интеграция включена' : 'Интеграция выключена');
      fetchIntegrations();
    } catch (err) {
      message.error('Ошибка изменения статуса');
    }
  };

  const handleDelete = async (serviceKey) => {
    try {
      await AdminService.deleteIntegration(serviceKey);
      message.success('Интеграция удалена');
      fetchIntegrations();
    } catch (err) {
      message.error('Ошибка удаления');
    }
  };

  const handleTestConnection = async (serviceKey) => {
    if (serviceKey !== 'nextcloud_storage') {
      message.info('Проверка доступна только для Nextcloud Хранилища');
      return;
    }
    
    try {
      setSaving(prev => ({ ...prev, [serviceKey + '_test']: true }));
      const res = await ExchangeService.testConnection();
      if (res.data.success) {
        message.success('Подключение успешно!');
      } else {
        message.error(`Ошибка: ${res.data.error}`);
      }
    } catch (err) {
      message.error('Ошибка проверки подключения');
    } finally {
      setSaving(prev => ({ ...prev, [serviceKey + '_test']: false }));
    }
  };

  const renderIntegrationForm = (integration) => {
    const currentData = integrations[integration.key];

    return (
      <Card
        key={integration.key}
        title={
          <Space>
            {integration.icon}
            <span>{integration.name}</span>
            {currentData && (
              <Tag color={currentData.is_active ? 'green' : 'default'}>
                {currentData.is_active ? 'Активна' : 'Неактивна'}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Switch
              checked={currentData?.is_active ?? false}
              onChange={(checked) => handleToggleActive(integration.key, checked)}
              disabled={!currentData}
            />
            {currentData && (
              <Popconfirm
                title="Удалить интеграцию?"
                description="Это действие нельзя отменить"
                onConfirm={() => handleDelete(integration.key)}
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            )}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Form
          layout="vertical"
          ref={(form) => { forms[integration.key] = form; }}
          initialValues={currentData?.config || {}}
          onFinish={() => handleSave(integration.key)}
        >
          {integration.fields.map(field => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={field.required ? [{ required: true, message: `Введите ${field.label.toLowerCase()}` }] : []}
            >
              {field.isPassword ? (
                <Input.Password placeholder={field.placeholder} />
              ) : (
                <Input placeholder={field.placeholder} />
              )}
            </Form.Item>
          ))}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving[integration.key]}
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: 'none',
                }}
              >
                Сохранить
              </Button>
              {integration.key === 'nextcloud_storage' && currentData && (
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={saving[integration.key + '_test']}
                  onClick={() => handleTestConnection(integration.key)}
                >
                  Проверить подключение
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>
    );
  };

  if (!isAdmin) {
    return (
      <div className="page-container">
        <Title level={3}>Доступ запрещён</Title>
        <Text>У вас нет прав для просмотра этой страницы</Text>
      </div>
    );
  }

  const tabItems = [
    ...INTEGRATIONS.map(integration => ({
      key: integration.key,
      label: (
        <Space>
          {integration.icon}
          {integration.name}
        </Space>
      ),
      children: renderIntegrationForm(integration),
    })),
    {
      key: 'user_credentials',
      label: (
        <Space>
          <TeamOutlined />
          Учётные данные пользователей
        </Space>
      ),
      children: (
        <Card title="Учётные данные Nextcloud для пользователей" style={{ marginBottom: 16 }}>
          <Form layout="vertical">
            <Form.Item label="Выберите пользователя">
              <Select
                showSearch
                placeholder="Выберите пользователя"
                optionFilterProp="children"
                onChange={handleUserChange}
                value={selectedUserId}
                allowClear
                style={{ width: '100%' }}
              >
                {users.map(user => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.full_name} ({user.email})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          
          {selectedUserId && (
            <>
              <Divider />
              <Form
                form={userCredsForm}
                layout="vertical"
                onFinish={handleSaveUserCredentials}
              >
                <Form.Item
                  name="nc_username"
                  label="Логин Nextcloud"
                  rules={[{ required: true, message: 'Введите логин Nextcloud' }]}
                >
                  <Input placeholder="username" />
                </Form.Item>
                <Form.Item
                  name="nc_password"
                  label="Пароль Nextcloud"
                  rules={[{ required: true, message: 'Введите пароль Nextcloud' }]}
                >
                  <Input.Password placeholder="Пароль" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      border: 'none',
                    }}
                  >
                    Сохранить учётные данные
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SettingOutlined style={{ fontSize: 28, color: '#6366f1' }} />
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Админ-панель</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Управление интеграциями с внешними сервисами</p>
          </div>
        </div>
      </div>

      <Divider />

      <Tabs
        defaultActiveKey={INTEGRATIONS[0].key}
        items={tabItems}
        tabPosition="left"
        style={{ minHeight: 500 }}
      />
    </div>
  );
}
